export interface ServiceOccurrenceLike {
  _id?: string | null;
  id?: string | null;
  title: string;
  price?: number | null;
  duration?: number | null;
  up?: boolean | null;
  description?: string | null;
  option?: {
    id?: string | null;
    title?: string | null;
    price?: number | null;
    duration?: number | null;
    description?: string | null;
  } | null;
}

export interface GroupedServiceOccurrence<T> {
  service: T;
  quantity: number;
  key: string;
}

const normalizedText = (value: string | null | undefined) =>
  value?.trim() || null;

const serviceIdentity = (service: ServiceOccurrenceLike) => {
  const id = normalizedText(service._id ?? service.id);
  const option = service.option;
  const optionId = normalizedText(option?.id);
  // Older review payloads can synthesize `${serviceId}:option` for every
  // option on a service. Treat that value as absent and use the immutable
  // option snapshot so different legacy options never collapse together.
  const stableOptionId =
    id && optionId === `${id}:option` ? null : optionId;

  return JSON.stringify([
    id,
    normalizedText(service.title),
    service.price ?? null,
    service.duration ?? null,
    Boolean(service.up),
    normalizedText(service.description),
    option
      ? [
          stableOptionId,
          normalizedText(option.title),
          option.price ?? null,
          option.duration ?? null,
          normalizedText(option.description),
        ]
      : null,
  ]);
};

/**
 * Groups only display-identical service occurrences. Callers keep their raw
 * arrays for totals, availability, analytics, and booking payloads.
 */
export const groupIdenticalServiceOccurrences = <
  T extends ServiceOccurrenceLike,
>(services: readonly T[]): GroupedServiceOccurrence<T>[] => {
  const groups = new Map<string, GroupedServiceOccurrence<T>>();

  services.forEach((service) => {
    const key = serviceIdentity(service);
    const existing = groups.get(key);

    if (existing) {
      existing.quantity += 1;
      return;
    }

    groups.set(key, { service, quantity: 1, key });
  });

  return Array.from(groups.values());
};
